import { useMemo } from "react";
import type { Itinerary } from "@b2bportal/ground-api";
import { Payment } from "@b2bportal/purchase-api";
import { useI18nContext } from "@hopper-b2b/i18n";
import { usePriceDisplay } from "@tenant/ui";

export const useCarReservationCheckoutBreakdown = (
  itinerary: Itinerary | null | undefined
) => {
  const { formatFiatCurrency } = useI18nContext();
  const baseRate = itinerary?.reservation?.baseRate;
  const totalCharge = itinerary?.reservation?.totalCharge;
  const fees = itinerary?.reservation?.fees;
  const payAtDesk = itinerary?.reservation?.payAtDesk;
  const paymentBreakdown = itinerary?.paymentBreakdown;
  const hasPayAtDesk = (payAtDesk?.value ?? 0) > 0;

  const total = usePriceDisplay({
    prices: totalCharge,
    formatOptions: {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    },
  });

  const {
    amountPaid,
    pointsRedeemed,
    pointsRedeemedFiat,
    travelCreditApplied,
    paymentMethodDisplay,
  } = useMemo(() => {
    if (!paymentBreakdown || !baseRate) {
      return {
        amountPaid: null,
        pointsRedeemed: null,
        pointsRedeemedFiat: undefined,
        travelCreditApplied: undefined,
        paymentMethodDisplay: null,
      };
    }

    // ground-api Payment enum doesn't include tenant-specific types like RbcAvionPoints
    const pointsRedeemed = paymentBreakdown.paymentLineItems.find(
      ({ type }) => (type as string) === Payment.RbcAvionPoints
    );

    const cardPayment = paymentBreakdown.paymentLineItems.find(
      ({ type }) => type === Payment.Card
    );

    const creditPaymentItem = paymentBreakdown.paymentLineItems.find(
      ({ type }) => (type as string) === Payment.TravelWalletCredit
    );

    const paymentMethodDisplay = cardPayment?.value?.cardNumberDisplay ?? null;

    const amountPaid = cardPayment
      ? {
          value: cardPayment.value.paymentAmount.amount,
          currencyCode: cardPayment.value.paymentAmount.currency,
        }
      : {
          value: 0,
          currencyCode: baseRate.currencyCode,
        };

    const flexFiat =
      pointsRedeemed?.value?.pointsRedemptionBreakdown
        ?.flexiblePointsCashEquivalent;
    const pointsRedeemedFiat = flexFiat?.value
      ? formatFiatCurrency({
          value: flexFiat.value,
          currencyCode: flexFiat.currencyCode ?? baseRate.currencyCode,
        })
      : undefined;

    const creditAmount = creditPaymentItem?.value?.paymentAmount?.amount ?? 0;
    const travelCreditApplied =
      creditPaymentItem && creditAmount > 0
        ? `-${formatFiatCurrency({
            value: creditAmount,
            currencyCode:
              creditPaymentItem.value?.paymentAmount?.currency ??
              baseRate.currencyCode,
          })}`
        : undefined;

    return {
      amountPaid,
      pointsRedeemed,
      pointsRedeemedFiat,
      travelCreditApplied,
      paymentMethodDisplay,
    };
  }, [baseRate, paymentBreakdown, formatFiatCurrency]);

  return {
    baseRate,
    fees,
    payAtDesk,
    amountPaid,
    pointsRedeemed,
    pointsRedeemedFiat,
    travelCreditApplied,
    total,
    hasPayAtDesk,
    paymentMethodDisplay,
  };
};
